import { request } from '../lib/apiClient'

/* ============================================================
   approvals.js — the approval chain for offer sheets, offers and requisitions.

   A request runs its steps in order: Department owner -> HR Administrator
   -> Management (CEO / VP, only above the salary / grade threshold). Any one
   eligible person decides a step. The server decides who can act; the
   `can_decide` flag on a request says whether the signed-in user can.

   Offer sheet (Create Offer): Management above 500k monthly gross.
   Offer (Place Offer letter): Department owner -> HR only.

   Per request, approvals.edit (HR Administrator by default) may choose the
   approvers: leave out a stage or add a named person — at Send for Approval
   (`approval_plan`, see planPayload) or later (addStep / removeStep).
   `can_edit_chain` on a request says whether the signed-in user can.
   ============================================================ */

/** GET /approvals/fetch — status 'pending' (waiting on me) | 'decided' (I acted). */
export function list({ status = 'pending', entityType, search, top, skip } = {}) {
  return request('/approvals/fetch', {
    params: {
      status,
      entity_type: entityType || undefined,
      search: search || undefined,
      top: top || undefined,
      skip: skip || undefined,
    },
  })
}

/** All approvals tab (System Administrator only): every request, any status. */
export function listAll({ status, entityType, search, top, skip } = {}) {
  return request('/approvals/all', {
    params: {
      status: status || undefined,
      entity_type: entityType || undefined,
      search: search || undefined,
      top: top || undefined,
      skip: skip || undefined,
    },
  })
}

/** The System Administrator sees every approval and can decide any waiting step. */
export function isSystemAdmin(user) {
  return String(user?.role_name || '').trim().toLowerCase().replace(/[\s-]+/g, '_') === 'system_administrator'
}

/** "Approved by A, B · date" for a decided request's row. */
export function decidedSummary(req) {
  if (!req || req.status === 'pending') return null
  const people = [...new Set((req.steps || [])
    .filter((s) => ['approved', 'rejected', 'covered'].includes(s.status) && s.decided_by_name)
    .map((s) => s.decided_by_name))]
  const verb = req.status === 'rejected' ? 'Rejected' : req.status === 'approved' ? 'Approved' : 'Closed'
  return people.length ? `${verb} by ${people.join(', ')}` : verb
}

/** Sidebar badge: requests waiting on me. */
export async function pendingCount() {
  try {
    const res = await request('/approvals/count')
    return res?.total ?? 0
  } catch {
    return 0
  }
}

/** One request with its full timeline. */
export function get(id) {
  return request('/approvals/fetch_by_id', { params: { id } })
}

/** Newest request for an offer / requisition (null when never sent for approval). */
export function forEntity(entityType, entityId) {
  return request('/approvals/entity', { params: { entity_type: entityType, entity_id: entityId } })
}

export function decide(requestId, decision, comment) {
  return request('/approvals/decide', {
    method: 'POST',
    body: { request_id: requestId, decision, comment: comment || undefined },
  })
}

/** The default chain a record would get, who decides each step, and
 *  whether I may change it: `{steps:[{stage,label,approvers,note}], can_edit_chain}`.
 *  An offer sheet not saved yet: no entityId, pass { jobPostId, gross }.
 *  A requisition not saved yet: no entityId, pass { departmentId, grade }. */
export function plan(entityType, entityId, { jobPostId, gross, departmentId, grade } = {}) {
  return request('/approvals/plan', {
    params: {
      entity_type: entityType,
      entity_id: entityId || undefined,
      job_post_id: jobPostId || undefined,
      gross: gross || undefined,
      department_id: departmentId || undefined,
      grade: grade || undefined,
    },
  })
}

/** Active staff (no candidates): approvers to add, and Create Offer's "Reporting to". */
export function people(search, top) {
  return request('/approvals/people', { params: { search: search || undefined, top: top || undefined } })
}

/** Add a column to a running request — one approval from any of `userIds` —
 *  after a step (default: at the end). */
export function addStep(requestId, userIds, afterStepId, stage) {
  return request('/approvals/steps/add', {
    method: 'POST',
    body: {
      request_id: requestId,
      user_ids: Array.isArray(userIds) ? userIds : [userIds],
      after_step_id: afterStepId || undefined,
      // 'management' ticks Management back on for a request started without it.
      stage: stage || undefined,
    },
  })
}

/** Set who a waiting / current column goes to (still one approval from any of them). */
export function setStepPeople(requestId, stepId, userIds) {
  return request('/approvals/steps/people', {
    method: 'POST',
    body: { request_id: requestId, step_id: stepId, user_ids: userIds },
  })
}

/** Take a waiting / current step out of a running request. */
export function removeStep(requestId, stepId, reason) {
  return request('/approvals/steps/remove', {
    method: 'POST',
    body: { request_id: requestId, step_id: stepId, reason: reason || undefined },
  })
}

/** Approvals block columns -> `approval_plan`; undefined keeps the default chain.
 *  columns = [{ stage, on, people: [{id,name}] | null, touched }] (ui/ApprovalPeople.jsx).
 *  people null = the stage's default people; one column = one approval from any of them. */
export function planPayload(columns) {
  if (!Array.isArray(columns) || !columns.length) return undefined
  const changed = columns.some((c) => c.stage === 'custom' || c.people || c.on !== c.defaultOn)
  if (!changed) return undefined
  return {
    columns: columns.map((c) => ({
      stage: c.stage,
      off: !c.on,
      people: c.people ? c.people.map((p) => p.id) : null,
    })),
  }
}

/** Grades for the requisition form, and which stop at HR (the rest also need Management). */
export function options() {
  return request('/approvals/options')
}

// ---- offer attachments ----------------------------------------------------

export function listAttachments(offerId) {
  return request('/offers/attachments/fetch', { params: { offer_id: offerId } })
}

export function uploadAttachment(offerId, file) {
  const fd = new FormData()
  fd.append('offer_id', offerId)
  fd.append('file', file, file.name)
  return request('/offers/attachments/upload', { method: 'POST', body: fd })
}

/** Short-lived link — `{data:{url}}`. */
export function openAttachment(id) {
  return request('/offers/attachments/open', { params: { id } })
}

export function deleteAttachment(id) {
  return request('/offers/attachments/delete', { method: 'DELETE', params: { id } })
}

/* Offer sheet (Create Offer) attachments — the documents the Approval Email
   lists, one `category` each (see PLACEMENT_ATTACHMENT_ITEMS). */
export function listPlacementAttachments(placementId) {
  return request('/offers/placements/attachments/fetch', { params: { placement_id: placementId } })
}

export function uploadPlacementAttachment(placementId, category, file) {
  const fd = new FormData()
  fd.append('placement_id', placementId)
  fd.append('category', category)
  fd.append('file', file, file.name)
  return request('/offers/placements/attachments/upload', { method: 'POST', body: fd })
}

/** Short-lived link — `{data:{url}}`. */
export function openPlacementAttachment(id) {
  return request('/offers/placements/attachments/open', { params: { id } })
}

export function deletePlacementAttachment(id) {
  return request('/offers/placements/attachments/delete', { method: 'DELETE', params: { id } })
}

export const STEP_STATUS = {
  waiting: { label: 'Up next', tone: 'b-gray' },
  pending: { label: 'Waiting', tone: 'b-amber' },
  approved: { label: 'Approved', tone: 'b-green' },
  rejected: { label: 'Rejected', tone: 'b-red' },
  skipped: { label: 'Skipped', tone: 'b-gray' },
  // Same person already approved an earlier step and also holds this one.
  covered: { label: 'Not needed separately', tone: 'b-green' },
  cancelled: { label: 'Cancelled', tone: 'b-gray' },
}

export const REQUEST_STATUS = {
  pending: { label: 'In approval', tone: 'b-amber' },
  approved: { label: 'Approved', tone: 'b-green' },
  rejected: { label: 'Rejected', tone: 'b-red' },
  cancelled: { label: 'Cancelled', tone: 'b-gray' },
}

/** Where the record lives in the app. */
export function recordPath(req) {
  if (!req) return null
  if (req.entity_type === 'offer') return `/offers?view=${req.entity_id}`
  if (req.entity_type === 'requisition') return `/requisitions?view=${req.entity_id}`
  if (req.entity_type === 'offer_placement') return `/offer-approval/${req.entity_id}`
  return null
}

export const ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg'
export const ATTACHMENT_MAX_MB = 10
